/**
 * The shell's one principal-aware API-error policy (H §4.5, §9.7). Screens call
 * `useApiErrorHandler()` in a rejection handler — it runs the side effects and returns what
 * to show — and render `ApiErrorState` for a failed load. `describeApiError` is the pure,
 * render-safe half.
 *
 * | Code                                     | Side effect                         | Shown                                 |
 * |------------------------------------------|-------------------------------------|---------------------------------------|
 * | `RESELLER_ACCESS_REVOKED`                | session cleared → Access removed    | (the session screen replaces the app) |
 * | `RESELLER_ROLE_FORBIDDEN`                | re-GET `/me`, toast                 | "Your role (Staff) can view…"         |
 * | `RESELLER_PATH_NOT_ALLOWED`              | console log (a console bug)         | generic error                         |
 * | `SAAS_AUTH_UNAVAILABLE` (503)            | none — NEVER a sign-out             | "Couldn't verify access. Retry."      |
 * | `429` (`Retry-After`)                    | none                                | wait, then Retry                      |
 *
 * `REFRESH_ORIGIN_UNROUTABLE` / `INVALID_ORIGIN` never reach a screen: `client.ts` ends the
 * session with the configuration-error screen. `TENANT_NOT_FOUND` belongs to the tenant
 * workspace (TMMRSL-0029).
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ApiError, backendCode, retryAfterSeconds } from '@/api/errors';
import { getAccessToken, signOut } from '@/auth/authStore';
import { useOptionalReseller } from '@/reseller/resellerContext';
import { useToast } from './useToast';

export interface ApiErrorDescription {
  message: string;
  /** Seconds before a retry makes sense (a `429`'s `Retry-After`). */
  retryAfterSeconds: number | undefined;
}

/** What to show for `err` — pure, safe to call while rendering. */
export function describeApiError(err: unknown, t: TFunction): ApiErrorDescription {
  const code = backendCode(err);
  const wait = retryAfterSeconds(err);
  if (code === 'SAAS_AUTH_UNAVAILABLE') {
    return { message: t('errors.saasAuthUnavailable'), retryAfterSeconds: wait };
  }
  if (code === 'RESELLER_ROLE_FORBIDDEN') {
    return {
      message: t('errors.roleForbidden', { role: t('roles.STAFF') }),
      retryAfterSeconds: wait,
    };
  }
  if (code === 'RESELLER_ACCESS_REVOKED') {
    return { message: t('auth.revokedBody'), retryAfterSeconds: wait };
  }
  if (code === 'PAYOUTS_MOVED_TO_LEDGER') {
    return { message: t('errors.PAYOUTS_MOVED_TO_LEDGER'), retryAfterSeconds: wait };
  }
  if (err instanceof ApiError) {
    if (err.code === 'RATE_LIMITED') {
      return {
        message:
          wait !== undefined
            ? t('errors.rateLimitedFor', { seconds: wait })
            : t('errors.RATE_LIMITED'),
        retryAfterSeconds: wait,
      };
    }
    if (code === 'RESELLER_PATH_NOT_ALLOWED') {
      return { message: t('errors.UNKNOWN'), retryAfterSeconds: wait };
    }
    return { message: t(`errors.${err.code}`), retryAfterSeconds: wait };
  }
  return { message: t('errors.UNKNOWN'), retryAfterSeconds: undefined };
}

/**
 * The error handler for a rejection: runs the table's side effects and returns the
 * description. Must be called within the shell (it toasts); outside `ResellerProvider` the
 * `/me` re-GET is skipped.
 */
export function useApiErrorHandler(): (err: unknown) => ApiErrorDescription {
  const { t } = useTranslation();
  const toast = useToast();
  const reseller = useOptionalReseller();

  return useCallback(
    (err: unknown) => {
      const code = backendCode(err);
      if (code === 'RESELLER_ACCESS_REVOKED' && getAccessToken() !== null) {
        signOut({ reason: 'revoked' });
      } else if (code === 'RESELLER_PATH_NOT_ALLOWED') {
        console.error('saas-auth refused a path resellers cannot use: RESELLER_PATH_NOT_ALLOWED');
      } else if (code === 'RESELLER_ROLE_FORBIDDEN') {
        const reload = reseller?.reloadMe() ?? Promise.resolve(null);
        void reload.then((role) => {
          toast(t('errors.roleForbidden', { role: t(`roles.${role ?? 'STAFF'}`) }), 'error');
        });
      }
      return describeApiError(err, t);
    },
    [t, toast, reseller],
  );
}
